import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  Table,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';

interface RefundRequestRejectDialogProps {
  open: boolean;
  onClose: () => void;
  request: any;
  onReject: (requestId: number, reviewNotes: string) => void;
}

const RefundRequestRejectDialog: React.FC<RefundRequestRejectDialogProps> = ({
  open,
  onClose,
  request,
  onReject
}) => {
  const [reviewNotes, setReviewNotes] = useState('');

  const handleClose = () => {
    setReviewNotes('');
    onClose();
  };

  const handleConfirm = () => {
    if (!reviewNotes.trim()) {
      return; // Button should be disabled, but extra safety
    }
    onReject(request.id, reviewNotes);
    handleClose();
  };

  if (!request) return null;

  const getItemTypeName = (type: string) => {
    const typeMap: { [key: string]: string } = {
      adSpace: 'Ad Space',
      tokenPlan: 'Token Plan',
      subscriptionPlan: 'Subscription Plan',
      purchasePlan: 'Purchase Plan'
    };
    return typeMap[type] || type;
  };

  const isNotesValid = reviewNotes.trim().length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Reject Refund Request - ID #{request.id}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Alert severity="warning">
            You are about to reject this refund request. The user will receive an email
            with your explanation.
          </Alert>

          <Box>
            <Typography variant="h6" gutterBottom>Request Summary</Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell><strong>User</strong></TableCell>
                  <TableCell>{request.user?.name || request.user?.email || `ID: ${request.userId}`}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Item Type</strong></TableCell>
                  <TableCell>{getItemTypeName(request.itemType)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Item Name</strong></TableCell>
                  <TableCell>{request.itemDetails?.itemName || 'N/A'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Refund Amount</strong></TableCell>
                  <TableCell sx={{ fontSize: '1.1em', fontWeight: 'bold' }}>
                    ${parseFloat(request.refundAmount).toFixed(2)} {request.itemDetails?.currencyCode || 'USD'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>User's Reason</strong></TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                    {request.reason || 'No reason provided'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Requested On</strong></TableCell>
                  <TableCell>{new Date(request.created_at).toLocaleString()}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ color: 'error.main' }}>
              Explanation for Rejection (Required) *
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              This explanation will be sent to the user. Please be clear and professional.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Explain why this refund request is being rejected..."
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              variant="outlined"
              required
              error={!isNotesValid && reviewNotes.length > 0}
              helperText={!isNotesValid && reviewNotes.length > 0 ? 'Explanation is required' : ''}
            />
          </Box>

          <Alert severity="info">
            After rejection:
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>The request status will be updated to "rejected"</li>
              <li>An email will be sent to the user with your explanation</li>
              <li>The user will not receive a refund</li>
            </ul>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={!isNotesValid}
        >
          Confirm Rejection
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RefundRequestRejectDialog;
